import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import moment from "moment";
import Header from "../../../Common/Layout/Header";

import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import "./SaleView.css";

const SaleView = () => {
  const { user, loading } = useAuth();
  const { invoiceId } = useParams(); // This is the MongoDB "id"
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const invoiceRef = useRef();

  const GetData = async () => {
    if (invoiceId) {
      try {
        const data = await apiService.getById(config.GETINVOICEBYID, invoiceId);
        setInvoice(data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch invoice details. Please try again.");
        console.error(err);
      }
    }
  };

  useEffect(() => {
    if (!loading && user && invoiceId) {
      GetData();
    }
  }, [user, loading, invoiceId]);

  const downloadPDF = async () => {
    if (!invoiceRef.current || !invoice) return;

    setIsGeneratingPDF(true);
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );

      const fileName = `invoice-${invoice.invoiceNumber}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const printInvoice = () => {
    window.print();
  };

  if (loading) return <p>Loading...</p>;

  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div id="layout-wrapper">
       <Header User={user} activeSection="accounts" />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-xl-12">
                <div className="card">
                  <div className="card-body">
                    {error && <div className="alert alert-danger">{error}</div>}

                    {!loading && !error && invoice && (
                      <div className="saleview-invoice-wrapper">
                        <div className="saleview-view-actions-bar">
                          <button
                            onClick={() => navigate("/salelist")}
                            className="btn btn-secondary"
                          >
                            <i className="ri-arrow-left-line align-bottom me-1" />{" "}
                            Back To List
                          </button>
                          <div>
                            <button
                              onClick={downloadPDF}
                              disabled={isGeneratingPDF}
                              className="btn btn-success me-2"
                            >
                              <i className="ri-download-line align-bottom me-1" />
                              {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
                            </button>
                            <button onClick={printInvoice} className="btn btn-info me-2">
                              <i className="ri-printer-line align-bottom me-1" />
                              Print
                            </button>
                            {/* <Link
                              to={`/editsaleinvoice/${invoice.id}`}
                              className="btn btn-primary"
                            >
                              <i className="ri-pencil-fill align-bottom me-1" /> Edit Invoice
                            </Link> */}
                          </div>
                        </div>

                        <div ref={invoiceRef} className="saleview-invoice-container" id="invoice-to-print">
                          <header className="saleview-invoice-header">
                            <div className="saleview-from-details">
                              <div className="saleview-company-logo">
                                {user.logo && (
                                  <img src={user.logo} alt="Logo" className="saleview-logo-img" />
                                )}
                              </div>
                              <h3 className="saleview-company-name">
                                {user.companyname || user.companyName}
                              </h3>
                              <p>{user.address}</p>
                              <p>{user.city}, {user.state} - {user.zipCode}</p>
                              <p><strong>Email:</strong> {user.email}</p>
                              <p><strong>Phone:</strong> {user.phoneNumber}</p>
                            </div>
                            <div className="saleview-invoice-title">
                              <h1>INVOICE</h1>
                            </div>
                          </header>

                          <section className="saleview-invoice-meta-details">
                            <div className="saleview-to-details">
                              <h4>BILL TO:</h4>
                              <p><strong>{invoice.partyName}</strong></p>
                              {/* Using partyGstin if available in Mongo */}
                              {invoice.partyGstin && <p>GST: {invoice.partyGstin}</p>}
                            </div>
                            <div className="saleview-meta-info">
                              <p><strong>Invoice No:</strong> <span>{invoice.invoiceNumber}</span></p>
                              <p><strong>Invoice Date:</strong> <span>{moment(invoice.date).format("DD-MMM-YYYY")}</span></p>
                              <p><strong>Due Date:</strong> <span>{invoice.dueDate ? moment(invoice.dueDate).format("DD-MMM-YYYY") : "N/A"}</span></p>
                              <p><strong>Status:</strong> 
                                <span className={`saleview-status-badge saleview-status-${invoice.status?.toLowerCase()}`}>
                                  {invoice.status}
                                </span>
                              </p>
                            </div>
                          </section>

                          <section className="saleview-invoice-items">
                            <table className="saleview-items-table">
                              <thead>
                                <tr>
                                  <th>S.No</th>
                                  <th>Item Description</th>
                                  <th className="saleview-text-center">Qty</th>
                                  <th className="saleview-text-right">Unit Price</th>
                                  <th className="saleview-text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
  {invoice.items && invoice.items.length > 0 ? (
    invoice.items.map((item, index) => (
      <tr key={index}>
        <td>{index + 1}</td>
        <td>{item.description}</td>
        <td className="saleview-text-center">{item.quantity}</td>
        {/* CHANGED: item.unitPrice -> item.rate */}
        <td className="saleview-text-right">
          {`${invoice.currencyCode} ${Number(item.rate || 0).toFixed(2)}`}
        </td>
        <td className="saleview-text-right">
          {`${invoice.currencyCode} ${(
            Number(item.quantity || 0) * Number(item.rate || 0)
          ).toFixed(2)}`}
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td>1</td>
      <td>Services / Reference: {invoice.transactionType}</td>
      <td className="saleview-text-center">1</td>
      <td className="saleview-text-right">
        {`${invoice.currencyCode} ${Number(invoice.baseAmount || 0).toFixed(2)}`}
      </td>
      <td className="saleview-text-right">
        {`${invoice.currencyCode} ${Number(invoice.baseAmount || 0).toFixed(2)}`}
      </td>
    </tr>
  )}
</tbody>
                            </table>
                          </section>

                          <footer className="saleview-invoice-footer">
                            <div className="saleview-notes-and-terms">
                              {invoice.notes && (<div><h4>Notes</h4><p>{invoice.notes}</p></div>)}
                              {invoice.terms && (<div><h4>Terms</h4><p>{invoice.terms}</p></div>)}
                            </div>
                            <div className="saleview-invoice-summary">
                              <div className="saleview-summary-row">
                                <span>Subtotal</span>
                                <span>{invoice.currencyCode} {Number(invoice.baseAmount).toFixed(2)}</span>
                              </div>
                              <div className="saleview-summary-row">
                                <span>Discount</span>
                                <span>-{invoice.currencyCode} {Number(invoice.discount).toFixed(2)}</span>
                              </div>
                              
                              {/* Dynamic Tax Breakdown from Swagger taxDetails object */}
                              {invoice.taxDetails && (
                                <>
                                   {invoice.taxDetails.cgstAmount > 0 && (
                                     <div className="saleview-summary-row saleview-tax-detail">
                                        <span>CGST</span>
                                        <span>+{invoice.currencyCode} {Number(invoice.taxDetails.cgstAmount).toFixed(2)}</span>
                                     </div>
                                   )}
                                   {invoice.taxDetails.sgstAmount > 0 && (
                                     <div className="saleview-summary-row saleview-tax-detail">
                                        <span>SGST</span>
                                        <span>+{invoice.currencyCode} {Number(invoice.taxDetails.sgstAmount).toFixed(2)}</span>
                                     </div>
                                   )}
                                   {invoice.taxDetails.igstAmount > 0 && (
                                     <div className="saleview-summary-row saleview-tax-detail">
                                        <span>IGST</span>
                                        <span>+{invoice.currencyCode} {Number(invoice.taxDetails.igstAmount).toFixed(2)}</span>
                                     </div>
                                   )}
                                   <div className="saleview-summary-row">
                                      <span>Total Tax</span>
                                      <span>+{invoice.currencyCode} {Number(invoice.taxDetails.totalTax).toFixed(2)}</span>
                                   </div>
                                </>
                              )}

                              <div className="saleview-summary-row saleview-grand-total">
                                <span>Grand Total</span>
                                <span>{invoice.currencyCode} {Number(invoice.grandTotal).toFixed(2)}</span>
                              </div>
                            </div>
                          </footer>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleView;